'use client';

/**
 * `useCreateProposalMutation` — facade over the v2 SDK's frontend-owned
 * market-creation orchestrator (`@morfi/backend-sdk-v2/client` →
 * `useCreateMarket`). Preserves the legacy mutation-style call surface
 * the create-proposal dialog uses (`mutate(params, { onSuccess, onError })`)
 * while plumbing the user's wallet through Turnkey signing on every
 * sequential tx (`create_market → prepare_outcomes` × N).
 *
 * Flow:
 *   1. Generate `marketNonce` + outcome-mint `Keypair[]` upfront, locally.
 *   2. Derive the deterministic Market PDA locally and read public chain
 *      config from `useGetPlatformConfig`.
 *   3. Delegate to `useCreateMarket.mutate(sdkParams, { onSuccess, onError })`
 *      with the SAME marketNonce + outcome-mint secret keys. The SDK posts
 *      the editorial snapshot to `POST /v1/markets` at click-Create time,
 *      then runs all 3+ chain stages and emits per-stage progress via the
 *      `progress` object.
 *
 * `activeWallet` is REQUIRED — callers must gate `mutate` behind an
 * authenticated session. Passing `null` causes `mutate` to call onError
 * with an `AUTH_REQUIRED` error instead of throwing in the orchestrator.
 */
import type { ActiveWallet, CreateMarketProgressState, CreateMarketResult, MarketEditorialCreateVars, SigningDeps, TurnkeyDeps, UploadMarketImagesResponse } from '@morfi/backend-sdk-v2/client';
import { hashDiagnosticEntity, readMarketConfig, useCreateMarket, useGetPlatformConfig, useMarketConfigCatalog, useQueryClient, useUploadMarketImages } from '@morfi/backend-sdk-v2/client';
import { BN, deriveMarketPda } from '@morfi/contract-sdk';
import { type Connection, Keypair, PublicKey } from '@solana/web3.js';
import { useTranslations } from 'next-intl';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import { useNetworkAwarePath } from '@/lib/network/use-network-aware-path';
import { logger } from '@/lib/telemetry/logger';
import { dismissToast, showToast } from '@/lib/ui/toast';
import type { CreateProposalResult } from './use-create-proposal-types';
export type { CreateProposalResult } from './use-create-proposal-types';
export type { CreateMarketProgressState };
export interface ProposalImageDraft {
  readonly croppedFile: File;
  readonly previewUrl: string;
}
export interface ProposalOutcomeInput {
  outcomeIndex: number;
  label: string;
  ticker: string;
  imageUrl: string;
  imageNsfw?: boolean;
  imageDraft?: ProposalImageDraft | null;
}
export interface CreateProposalMutationParams {
  title: string;
  description: string;
  category: string;
  location: string;
  ticker: string;
  coverImageUrl: string;
  coverImageNsfw?: boolean;
  coverImageDraft?: ProposalImageDraft | null;
  outcomes: ProposalOutcomeInput[];
  /** When true, suppress the success toast — the caller renders its own
   *  confirmation UI. Errors and in-flight toast still surface. */
  silentSuccess?: boolean;
}
export interface UseCreateProposalApi {
  mutate: (params: CreateProposalMutationParams, operation: {
    wallet: ActiveWallet;
    onSuccess?: (result: CreateProposalResult) => void;
    onError?: (error: Error) => void;
  }) => void;
  isPending: boolean;
  error: Error | null;
  reset: () => void;
  /** Per-stage progress — re-renders on every SDK orchestrator emit. */
  progress: CreateMarketProgressState;
}
export interface UseCreateProposalDeps {
  readonly connection: Connection;
  readonly submitTransaction: Parameters<typeof useCreateMarket>[0]['submitTransaction'];
  /**
   * Active wallet or null. When null, `mutate()` immediately rejects via
   * onError with an `AUTH_REQUIRED` error — the orchestrator is NEVER
   * invoked with an empty pubkey.
   */
  readonly activeWallet: ActiveWallet | null;
  readonly signingDeps: SigningDeps;
  readonly turnkeyDeps: TurnkeyDeps;
}

/**
 * Pick a fresh u64 market nonce as a decimal string. Uses `crypto.
 * getRandomValues` so the value is unpredictable across sessions.
 */
function freshMarketNonceDecimal(): string {
  const buf = new Uint8Array(8);
  crypto.getRandomValues(buf);
  return new BN(buf, 'le').toString(10);
}
type MarketImageUploadSlot = {
  readonly kind: 'cover';
} | {
  readonly kind: 'outcome';
  readonly index: number;
};
interface MarketImageUploadPlan {
  readonly files: readonly File[];
  readonly slots: readonly MarketImageUploadSlot[];
}
interface MarketImagePayload {
  readonly coverImageUrl: string;
  readonly coverImageNsfw?: boolean;
  readonly outcomes: readonly ProposalOutcomeInput[];
}
interface RequiredMarketChainConfig {
  readonly marketConfig: string;
  readonly quoteMint: string;
}
function requireMarketChainConfig(config: {
  readonly marketConfig: string | undefined;
  readonly quoteMint: string | undefined;
}): RequiredMarketChainConfig {
  if (config.marketConfig === undefined || config.quoteMint === undefined) {
    throw new Error('CHAIN_CONFIG_UNAVAILABLE: marketConfig or quoteMint is missing');
  }
  return {
    marketConfig: config.marketConfig,
    quoteMint: config.quoteMint
  };
}
function buildMarketImagePayload(coverImageUrl: string, coverImageNsfw: boolean | undefined, outcomes: readonly ProposalOutcomeInput[]): MarketImagePayload {
  if (coverImageNsfw === undefined) return {
    coverImageUrl,
    outcomes
  };
  return {
    coverImageUrl,
    coverImageNsfw,
    outcomes
  };
}
function buildMarketImageUploadPlan(params: CreateProposalMutationParams): MarketImageUploadPlan {
  const files: File[] = [];
  const slots: MarketImageUploadSlot[] = [];
  if (params.coverImageDraft !== undefined && params.coverImageDraft !== null) {
    files.push(params.coverImageDraft.croppedFile);
    slots.push({
      kind: 'cover'
    });
  }
  params.outcomes.forEach((outcome, index) => {
    if (outcome.imageDraft === undefined || outcome.imageDraft === null) return;
    files.push(outcome.imageDraft.croppedFile);
    slots.push({
      kind: 'outcome',
      index
    });
  });
  return {
    files,
    slots
  };
}
function applyUploadedMarketImageUrls(params: CreateProposalMutationParams, plan: MarketImageUploadPlan, upload: UploadMarketImagesResponse): MarketImagePayload {
  let coverImageUrl = params.coverImageUrl;
  let coverImageNsfw = params.coverImageNsfw;
  const outcomes = params.outcomes.map(outcome => ({
    ...outcome
  }));
  plan.slots.forEach((slot, slotIndex) => {
    const uploaded = upload.images[slotIndex];
    if (uploaded === undefined) {
      throw new Error('MARKET_IMAGE_UPLOAD_INVALID_RESPONSE: missing uploaded image');
    }
    if (uploaded.index !== slotIndex) {
      throw new Error('MARKET_IMAGE_UPLOAD_INVALID_RESPONSE: upload order mismatch');
    }
    if (uploaded.validationStatus === 'minors_takedown') {
      throw new Error('MARKET_IMAGE_REJECTED: image did not pass moderation');
    }
    if (slot.kind === 'cover') {
      coverImageUrl = uploaded.url;
      coverImageNsfw = uploaded.validationStatus !== 'clean';
      return;
    }
    const outcome = outcomes[slot.index];
    if (outcome === undefined) {
      throw new Error('MARKET_IMAGE_UPLOAD_INVALID_RESPONSE: outcome slot missing');
    }
    outcomes[slot.index] = {
      ...outcome,
      imageUrl: uploaded.url,
      imageNsfw: uploaded.validationStatus !== 'clean'
    };
  });
  return buildMarketImagePayload(coverImageUrl, coverImageNsfw, outcomes);
}
export function useCreateProposalMutation(deps: UseCreateProposalDeps): UseCreateProposalApi {
  // `useCreateMarket` needs a non-null wallet
  // to construct, so we synthesise a placeholder pubkey when unauth'd —
  // but `mutate()` short-circuits to AUTH_REQUIRED before ever calling
  // through, so the placeholder is never reached at runtime.
  const create = useCreateMarket({
    connection: deps.connection,
    submitTransaction: deps.submitTransaction,
    activeWallet: deps.activeWallet ?? {
      id: '__unauth__',
      address: '11111111111111111111111111111111',
      type: 'embedded'
    },
    signingDeps: deps.signingDeps,
    turnkeyDeps: deps.turnkeyDeps,
    logger
  });
  const chainConfigQuery = useGetPlatformConfig({
    kind: 'market',
    fields: ['marketConfig']
  });
  const catalog = useMarketConfigCatalog();
  const queryClient = useQueryClient();
  const uploadMarketImages = useUploadMarketImages();
  const networkPath = useNetworkAwarePath();
  // Root translator: used for this hook's loading/success snackbars (full
  // dotted `launchpad.form.*` keys) AND for the shared API-error resolver, so
  // the market-creation error toast shows the same translated copy the global
  // funnel resolves instead of a raw, untranslated `err.message`.
  // ALLOW-UNKNOWN: next-intl `t` is typed against the static message tree; the resolver dispatches dynamic SDK-emitted keys, so we narrow the function shape (not the value).
  const t = useTranslations() as unknown as DynamicTranslator;
  function mutate(params: CreateProposalMutationParams, operation: {
    wallet: ActiveWallet;
    onSuccess?: (result: CreateProposalResult) => void;
    onError?: (error: Error) => void;
  }): void {
    const configuredWallet = deps.activeWallet;
    if (configuredWallet === null) {
      const err = new Error('AUTH_REQUIRED: sign in to create a market');
      operation.onError?.(err);
      return;
    }
    const activeWallet = operation.wallet;
    if (configuredWallet.id !== activeWallet.id || configuredWallet.address !== activeWallet.address || configuredWallet.type !== activeWallet.type) {
      logger.error('market_creation.wallet_identity_mismatch', {
        stage: 'preparing',
        selectedWalletIdHash: hashDiagnosticEntity(activeWallet.id),
        selectedWalletAddressHash: hashDiagnosticEntity(activeWallet.address),
        configuredWalletIdHash: hashDiagnosticEntity(configuredWallet.id),
        configuredWalletAddressHash: hashDiagnosticEntity(configuredWallet.address)
      });
      operation.onError?.(new Error('WALLET_IDENTITY_MISMATCH'));
      return;
    }
    const run = async (): Promise<void> => {
      showToast({
        id: TOAST_IDS.TX_SUBMITTING,
        message: t('launchpad.form.creatingMarket'),
        variant: 'loading',
        duration: Infinity
      });
      try {
        // 1. Generate marketNonce + outcome-mint keypairs locally. The same
        //    values get threaded into the click-Create editorial snapshot and
        //    the on-chain txs.
        const marketNonceDecimal = freshMarketNonceDecimal();
        const outcomeMints = params.outcomes.map(() => Keypair.generate());
        const [marketPda] = deriveMarketPda(new PublicKey(activeWallet.address), new BN(marketNonceDecimal, 10));
        const marketPubkey = marketPda.toBase58();
        let chainConfig = chainConfigQuery.data;
        if (chainConfig?.marketConfig === undefined) {
          const refreshed = await chainConfigQuery.refetch();
          chainConfig = refreshed.data;
        }
        const address = chainConfig?.marketConfig;
        let economics = readMarketConfig(queryClient, address);
        if (economics === undefined && address !== undefined) {
          await catalog.refetch();
          economics = readMarketConfig(queryClient, address);
        }
        const requiredChainConfig = requireMarketChainConfig({
          marketConfig: address,
          quoteMint: economics?.quoteMint
        });
        const imageUploadPlan = buildMarketImageUploadPlan(params);
        const imagePayload = imageUploadPlan.files.length > 0 ? applyUploadedMarketImageUrls(params, imageUploadPlan, await uploadMarketImages.mutateAsync({
          files: imageUploadPlan.files
        })) : buildMarketImagePayload(params.coverImageUrl, params.coverImageNsfw, params.outcomes);
        const editorialCreatePayload: MarketEditorialCreateVars = {
          title: params.title,
          description: params.description,
          category: params.category,
          location: params.location,
          ticker: params.ticker,
          creatorPubkey: activeWallet.address,
          // ALLOW-NSFW-STRIP: outbound create payload — moderation runs server-side.
          coverImageUrl: imagePayload.coverImageUrl,
          outcomes: imagePayload.outcomes.map((o, i) => ({
            outcomeIndex: o.outcomeIndex,
            label: o.label,
            ticker: o.ticker,
            // ALLOW-NSFW-STRIP: outbound create payload — moderation runs server-side.
            imageUrl: o.imageUrl,
            outcomeMintBase58: outcomeMints[i]?.publicKey.toBase58() ?? ''
          })),
          marketNonceDecimal
        };

        // 2. Run the on-chain lifecycle via the SDK orchestrator. It first
        //    posts the click-Create editorial snapshot, then submits chain
        //    txs with the same nonce + outcome-mint secret keys.
        await create.mutate({
          ticker: params.ticker,
          marketUri: `morfi://market/${marketPubkey}`,
          outcomes: params.outcomes.map((o_0, i_0) => ({
            name: `${params.ticker} ${o_0.ticker}`,
            symbol: o_0.ticker,
            ticker: o_0.ticker,
            uri: `morfi://market/${marketPubkey}/outcome/${String(i_0)}`
          })),
          quoteMintBase58: requiredChainConfig.quoteMint,
          marketConfigBase58: requiredChainConfig.marketConfig,
          marketNonceDecimal,
          outcomeMintSecretKeys: outcomeMints.map(k => k.secretKey),
          editorialCreatePayload
        }, {
          onSuccess: (sdkResult: CreateMarketResult): void => {
            const result: CreateProposalResult = {
              marketPubkey: sdkResult.marketPubkey,
              chainSignatures: {
                shell: sdkResult.shellSignature,
                outcomes: [...sdkResult.outcomeSignatures],
                finalize: sdkResult.finalizeSignature
              }
            };
            dismissToast(TOAST_IDS.TX_SUBMITTING);
            if (params.silentSuccess !== true) {
              showToast({
                id: TOAST_IDS.PROPOSAL_CREATED,
                message: t('launchpad.form.marketCreatedSuccess'),
                description: t('launchpad.form.marketCreatedSuccessDescription'),
                variant: 'success',
                duration: 8000,
                action: {
                  label: t('launchpad.detail.viewMarket'),
                  onClick: (): void => {
                    window.location.href = networkPath(`/proposals/${sdkResult.marketPubkey}`);
                  }
                }
              });
            }
            onEvent('proposal_created', {
              proposalId: sdkResult.marketPubkey,
              category: params.category,
              numOutcomes: params.outcomes.length,
              fundAmount: 0
            });
            operation.onSuccess?.(result);
          },
          onError: (err_0: Error): void => {
            dismissToast(TOAST_IDS.TX_SUBMITTING);
            showToast({
              id: TOAST_IDS.PROPOSAL_CREATE_ERROR,
              message: resolveApiErrorMessage(err_0, t) ?? t('errors.unknown'),
              variant: 'error',
              duration: Infinity,
              cancel: {
                label: t('tx.toast.closeButton'),
                onClick: (): void => {
                  dismissToast(TOAST_IDS.PROPOSAL_CREATE_ERROR);
                }
              }
            });
            operation.onError?.(err_0);
          }
        });
      } catch (setupErr: unknown) {
        const wrapped = setupErr instanceof Error ? setupErr : new Error(String(setupErr));
        logger.error('market_creation.prepare_failed', {
          errorClass: setupErr instanceof Error ? 'Error' : typeof setupErr
        });
        dismissToast(TOAST_IDS.TX_SUBMITTING);
        showToast({
          id: TOAST_IDS.PROPOSAL_CREATE_ERROR,
          message: resolveApiErrorMessage(wrapped, t) ?? t('errors.unknown'),
          variant: 'error',
          duration: Infinity,
          cancel: {
            label: t('tx.toast.closeButton'),
            onClick: (): void => {
              dismissToast(TOAST_IDS.PROPOSAL_CREATE_ERROR);
            }
          }
        });
        operation.onError?.(wrapped);
      }
    };
    void run();
  }
  return {
    mutate,
    isPending: create.isPending || chainConfigQuery.isLoading || uploadMarketImages.isPending,
    error: create.error ?? uploadMarketImages.error,
    reset: create.reset,
    progress: create.progress
  };
}